import { Observable } from 'rxjs';

/** Part of a paragraph: plain text, a bold name, or a red badge (like LIVE). */
export type ConfirmationTextPart = string | { strong: string } | { badge: string };

/** One paragraph of the message: plain text, or text made of parts. */
export type ConfirmationParagraph = string | ConfirmationTextPart[];

/** data-testId values for the dialog parts, so the e2e selectors of the dialogs it replaced keep working. */
export interface ConfirmationTestIds {
  container?: string;
  title?: string;
  message?: string;
  warning?: string;
  progress?: string;
  cancel?: string;
  confirm?: string;
}

export interface ConfirmationOptions {
  title: string;
  /** One paragraph, or several. */
  message: string | ConfirmationParagraph[];
  /** Orange warning line under the message. */
  warning?: string;
  confirmLabel: string;
  /** Defaults to Cancel. */
  cancelLabel?: string;
  /** Only the action button (a notice with Close). */
  hideCancel?: boolean;
  /** Red confirm button, for actions that delete or break something. Green otherwise. */
  destructive?: boolean;
  /**
   * Runs when the user confirms. The dialog shows progress and closes when it completes; on error it shows a
   * snackbar and stays open.
   */
  action?: () => Observable<unknown>;
  /** Snackbar shown after the action completes. */
  successMessage?: string;
  /** Snackbar text when the action fails. Defaults to the error's message. */
  errorMessage?: string | ((error: any) => string);
  /** Escape and clicks outside do not close the dialog. */
  disableClose?: boolean;
  testIds?: ConfirmationTestIds;
}

/** testIds used when the caller gives none. */
export const DEFAULT_CONFIRMATION_TEST_IDS: ConfirmationTestIds = {
  container: 'confirmation-dialog',
  title: 'confirmation-dialog-title',
  message: 'confirmation-dialog-message',
  warning: 'confirmation-dialog-warning',
  progress: 'confirmation-dialog-progress',
  cancel: 'confirmation-dialog-cancel-button',
  confirm: 'confirmation-dialog-confirm-button',
};

/** testIds of the former quick confirmation dialog (e2e QuickConfirmationDialogPageObject). */
export const QUICK_CONFIRMATION_TEST_IDS: ConfirmationTestIds = {
  title: 'quick-confirmation-dialog-title',
  message: 'quick-confirmation-dialog-body',
  cancel: 'quick-confirmation-dialog-cancel-button',
  confirm: 'quick-confirmation-dialog-action-button',
};

/** testIds of the DialogService frame the EPG and web player confirmations had (e2e DialogPageObject). */
export const DIALOG_FRAME_TEST_IDS: ConfirmationTestIds = {
  container: 'epg-dialog-container',
  title: 'epg-dialog-title',
  message: 'epg-dialog-content',
  cancel: 'epg-dialog-button-cancel',
  confirm: 'epg-dialog-button-delete',
};

/** testIds of the former Notice message dialog. */
export const NOTICE_TEST_IDS: ConfirmationTestIds = {
  message: 'message-dialog-content',
  confirm: 'message-dialog-close-button',
};

/** A paragraph part ready for the template. */
export interface ConfirmationPart {
  kind: 'text' | 'strong' | 'badge';
  text: string;
}
